// Iter 122 — central registry of every navigable destination in the app.
// Used by (1) hub landing-page tile grids, and (2) the ⌘K command palette.
//
// Each destination = { id, label, blurb, href, hub, icon, keywords, role }
//   role: 'all' | 'pm' (only Practice Managers/Admins see it)

import {
  Stamp, Droplet, ClipboardList,                // clinical
  BookOpen, ClipboardCheck, Activity, Users2, Sparkles,   // knowledge
  CalendarDays, HardDrive, UserCircle2, FileSignature, Settings2, Trophy, Receipt,  // operations
  Shield,                                       // compliance
  HelpCircle,                                    // help
} from 'lucide-react';

export const DESTINATIONS = [
  // ── Clinical ────────────────────────────────────────────────
  { id: 'consent', hub: 'clinical', icon: Stamp,
    label: 'Consent forms',
    blurb: 'Procedure-specific consent + post-procedure care leaflets',
    href: '/consent', role: 'all',
    keywords: ['informed consent', 'risks', 'pil', 'leaflet'] },
  { id: 'anticoag', hub: 'clinical', icon: Droplet,
    label: 'Anticoagulation',
    blurb: 'Pre-procedure anticoagulant decision engine',
    href: '/anticoag', role: 'all',
    keywords: ['warfarin', 'apixaban', 'doac', 'aspirin', 'bleeding'] },
  { id: 'preparation', hub: 'clinical', icon: ClipboardList,
    label: 'Patient Prep',
    blurb: 'Procedure-by-procedure preparation instructions',
    href: '/preparation', role: 'all',
    keywords: ['fasting', 'bowel prep', 'contrast', 'before'] },

  // ── Knowledge ───────────────────────────────────────────────
  { id: 'sops', hub: 'knowledge', icon: ClipboardCheck,
    label: 'SOPs',
    blurb: 'Standard operating procedures library & assignments',
    href: '/sops', role: 'all',
    keywords: ['policy', 'procedure', 'protocol', 'sign off'] },
  { id: 'training', hub: 'knowledge', icon: BookOpen,
    label: 'Kestral K4',
    blurb: 'In-house training modules + AI assistant chat',
    href: '/k4', role: 'all',
    keywords: ['k4', 'training', 'learning', 'kestral'] },
  { id: 'mbs', hub: 'knowledge', icon: Activity,
    label: 'MBS Monitor',
    blurb: 'Medicare item-code monitoring + alerts',
    href: '/mbs', role: 'all',
    keywords: ['medicare', 'billing', 'item number', 'mbs'] },
  { id: 'referrals', hub: 'knowledge', icon: Users2,
    label: 'Referral Base',
    blurb: 'GP & specialist directory with bulk import',
    href: '/referrals', role: 'all',
    keywords: ['gp', 'specialist', 'doctor', 'referrer'] },
  { id: 'quick-ref', hub: 'knowledge', icon: Sparkles,
    label: 'Quick Reference',
    blurb: 'Search anything — drugs, doses, protocols, contacts',
    href: '/quick-ref', role: 'all',
    keywords: ['search', 'lookup', 'drug', 'dose'] },
  { id: 'help', hub: 'knowledge', icon: HelpCircle,
    label: 'Help & guides',
    blurb: 'Step-by-step walk-throughs for every feature',
    href: '/help', role: 'all',
    keywords: ['help', 'guide', 'tutorial', 'how to', 'walkthrough', 'docs', 'support'] },

  // ── Operations ──────────────────────────────────────────────
  { id: 'roster', hub: 'operations', icon: CalendarDays,
    label: 'Rostering',
    blurb: 'Shifts, leave, timesheets, swaps, public schedule',
    href: '/roster', role: 'all',
    keywords: ['shift', 'roster', 'timesheet', 'leave', 'swap', 'schedule'] },
  { id: 'equipment', hub: 'operations', icon: HardDrive,
    label: 'Equipment register',
    blurb: 'Inventory, service due dates, compliance status',
    href: '/equipment', role: 'all',
    keywords: ['asset', 'service', 'calibration', 'maintenance'] },
  { id: 'people', hub: 'operations', icon: UserCircle2,
    label: 'People',
    blurb: 'Staff directory & licensing',
    href: '/people', role: 'pm',
    keywords: ['staff', 'user', 'team', 'ahpra', 'license'] },
  { id: 'letterhead', hub: 'operations', icon: FileSignature,
    label: 'Letterhead Generator',
    blurb: 'Branded letters to referring doctors with map + QR',
    href: '/letterhead', role: 'pm',
    keywords: ['letter', 'referrer', 'communication'] },
  { id: 'invoices', hub: 'operations', icon: Receipt,
    label: 'Invoicing',
    blurb: 'Tax invoices for teleradiology suppliers · PDF + CSV',
    href: '/invoices', role: 'pm',
    keywords: ['bill', 'invoice', 'gst', 'tax', 'teleradiologist', 'supplier', 'pay'] },
  { id: 'golive', hub: 'operations', icon: Trophy,
    label: 'Go-Live Tracker',
    blurb: '161-task practice opening checklist',
    href: '/golive', role: 'pm',
    keywords: ['launch', 'opening', 'go live', 'checklist'] },
  { id: 'management', hub: 'operations', icon: Settings2,
    label: 'Practice settings',
    blurb: 'Admin tools, charters, user management',
    href: '/management', role: 'pm',
    keywords: ['admin', 'settings', 'config', 'practice'] },
  { id: 'permissions', hub: 'operations', icon: Settings2,
    label: 'Permissions',
    blurb: 'Control who sees which sections — by role or by user',
    href: '/permissions', role: 'pm',
    keywords: ['rbac', 'access', 'role', 'visibility', 'permission'] },

  // ── Compliance (its own hub — the existing /compliance page) ──
  { id: 'compliance', hub: 'compliance', icon: Shield,
    label: 'Compliance Hub',
    blurb: 'DIAS readiness, equipment audit, licensing dashboards',
    href: '/compliance', role: 'pm',
    keywords: ['dias', 'audit', 'qip', 'accreditation', 'governance'] },
];

export function destinationsForHub(hubId, isPM) {
  return DESTINATIONS.filter(d => d.hub === hubId && (d.role === 'all' || isPM));
}
